import { type ButtonHTMLAttributes, type MouseEvent, type ReactNode, type Ref, forwardRef, type JSX } from 'react';
import classNames from 'classnames';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import ChevronSm from '../../icons/ChevronSm';
import Chevron from '../../icons/Chevron';
import { Size } from '../../types';
import styles from './Dropdown.module.scss';

export type DropdownProps = {
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  leadingIcon?: IconDefinition<'color' | 'orientation'>;
  onClick?: (e?: MouseEvent<HTMLButtonElement>) => void;
  size?: Size<'sm' | 'md'>;
  testId?: string;
  text: ReactNode;
  title?: string;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  variant?: 'default' | 'secondary' | 'flat';
  width?: 'auto' | 'full';
  'aria-expanded'?: boolean;
  'aria-haspopup'?: 'menu' | 'listbox' | 'dialog' | boolean;
  'aria-controls'?: string;
};

const Dropdown = (
  {
    ariaLabel,
    className,
    disabled,
    leadingIcon,
    onClick,
    size = 'md',
    testId,
    text,
    title,
    type = 'button',
    variant = 'default',
    width = 'auto',
    'aria-expanded': ariaExpanded,
    'aria-haspopup': ariaHasPopup = 'menu',
    'aria-controls': ariaControls
  }: DropdownProps,
  ref: Ref<HTMLButtonElement>
): JSX.Element => {
  const onButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(e);
    }
  };

  const classes = classNames(className, styles.dropdown, styles[size], styles[variant], styles[width], disabled && styles.disabled);

  return (
    <button
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup}
      aria-controls={ariaControls}
      className={classes}
      onClick={onButtonClick}
      disabled={disabled}
      data-testid={testId}
      title={title}
      type={type}
      ref={ref}
    >
      <div className={styles.contents}>
        {leadingIcon ? (
          <div className={styles.leadingIcon}>
            <IconRenderer icon={leadingIcon} defaults={{ color: 'currentColor', size: size === 'sm' ? 'sm' : 'md' }} />
          </div>
        ) : null}
        <span className={styles.text}>{text}</span>
      </div>
      <div className={styles.chevron}>
        {size == 'sm' ? (
          <ChevronSm color="currentColor" orientation={ariaExpanded ? 'up' : 'down'} />
        ) : (
          <Chevron color="currentColor" orientation={ariaExpanded ? 'up' : 'down'} />
        )}
      </div>
    </button>
  );
};

export default forwardRef(Dropdown);
